import { useRef, useState } from "react";
import React from "react";

import { MultipleUnitsContainer } from "../MultipleUnits/MultipleUnitsContainer";
import { SingleUnitContainer } from "../SingleUnitContainer";
import { AttachmentFormLayoutContainer } from "../AttachmentFormLayout/AttachmentFormLayoutContainer";
import { UnitId } from "../UnitId/UnitId";
import { AbsoluteModelPath, AbsoluteResolvedModelPath, PathTokens } from "../../../../../../Common/ModelPath/ModelPath";
import { OpenAttachmentAction } from "../../AttachmentForm/AttachmentForm";
import { useFormObject } from "../../../FormObjectProvider";
import { DownloadedFileInfo } from "../AttachmentFormLayout/AttachmentFormLayout";
import { ExtraPageAction, PropsPageActions } from "../Types";
import { IUnitRegistry } from "../../Navigation/UnitRegistry/IUnitRegistry";

export interface AttachmentFormUnitProps {
    gfv: string;
    unitId: UnitId;
    unitRegistry: IUnitRegistry;
    bindingPath: AbsoluteResolvedModelPath;
    updateEmptyUnits: (unitId: UnitId, empty: boolean) => void;
    caption: string;
    description: string;
    onRemoveAttachment: (bindingPath: AbsoluteResolvedModelPath) => Promise<void>;
    optional?: boolean;
    multiple?: boolean;
    disabled?: boolean;
    evaluatorsContextPath: AbsoluteModelPath;
    hasDefaultContent?: boolean;
    onAddPage: () => void;
    onHighlightUnit: (unitId: UnitId) => void;
    extraPageActions?: ExtraPageAction[];
    pageActions: PropsPageActions;
    visible?: boolean;
    isReadOnly?: boolean;
    onMount: () => void;
    onUnmount: () => void;
    onClearAttachment: (attachmentId: string) => Promise<void>;
    attachmentUnitItemInfoPaths?: AbsoluteResolvedModelPath | AbsoluteResolvedModelPath[];
    emptyUnitTitle?: string;
    emptyUnitDescription?: React.ReactNode;
    children: React.ReactChildren | React.ReactNode;
}

export const AttachmentFormUnit = (props: AttachmentFormUnitProps) => {
    const onRemoveMultipleUnit = async (instance: string) => {
        const bindingPath = props.bindingPath.joinWith(instance);

        await props.onRemoveAttachment(bindingPath);
    };
    const form = useFormObject();
    const { uiUtil } = form.getHelperFunctionsCallContext();

    const attachmentInfo = new Map<string, DownloadedFileInfo>();
    const attachmentInfoRef = useRef(attachmentInfo);

    const extraPageActions = props.extraPageActions?.map(action => ({
        ...action,
        "data-tid": action.tid,
        handler: (instancePath?: string) =>
            action.handler({
                publicForm: form.publicForm,
                instancePath: instancePath,
                uiUtil: uiUtil,
                attachmentGfv: props.gfv,
                isAttachmentFile: instancePath ? attachmentInfoRef.current.get(instancePath)?.isFile : false,
                contentUri: instancePath ? attachmentInfoRef.current.get(instancePath)?.contentUri : "",
                fileName: instancePath ? attachmentInfoRef.current.get(instancePath)?.filename : "",
            }),
    }));

    const [openAttachmentAction, setOpenAttachmentAction] = useState<OpenAttachmentAction | null>(null);

    if (props.visible === false) {
        return <></>;
    }

    if (props.multiple) {
        return (
            <>
                <MultipleUnitsContainer
                    updateEmptyUnits={props.updateEmptyUnits}
                    unitId={props.unitId}
                    name={props.gfv}
                    hidden={false}
                    bindingPath={props.bindingPath}
                    layoutKind={"formInForm"}
                    pageActions={props.pageActions ?? {}}
                    extraPageActions={extraPageActions}
                    pages={[]}
                    optional={props.optional}
                    hasDefaultContent={true}
                    isReadOnly={props.isReadOnly}
                    showUnitBorder={true}
                    unitRegistry={props.unitRegistry}
                    noContent={false}
                    onRemoveUnit={onRemoveMultipleUnit}
                    clearConfirmationSettings={{
                        removeChildrenText: `Удалить ${props.caption}`,
                        keepChildrenCustomText: `Скопировать ${props.caption}`,
                        clearChildrenCustomText: `Очистить ${props.caption}`,
                    }}
                    unitsWidth={{}}
                    onHighlightUnit={props.onHighlightUnit}
                    onMount={props.onMount}
                    onUnmount={props.onUnmount}
                    hideEnableAutoValues={true}
                    excludedPageActions={["copy"]}
                    modalTitle={props.caption}>
                    <AttachmentFormLayoutContainer
                        {...props}
                        extraPageActions={extraPageActions}
                        bindingPath={props.bindingPath.joinWith(PathTokens.each)}
                        openAttachmentAction={openAttachmentAction}
                        setOpenAttachmentAction={setOpenAttachmentAction}
                        attachmentInfoRef={attachmentInfoRef}
                    />
                </MultipleUnitsContainer>
                {props.children}
            </>
        );
    }

    const onRemovePage = async () => {
        await props.onRemoveAttachment(props.bindingPath);
    };

    const onRemoveSingleAttachment = async () => {
        await props.onRemoveAttachment(props.bindingPath);
    };

    return (
        <>
            <SingleUnitContainer
                noContent={false}
                unitId={props.unitId}
                bindingPath={props.bindingPath}
                unitRegistry={props.unitRegistry}
                layoutKind={"formInForm"}
                optional={props.optional}
                pages={[]}
                clearConfirmationSettings={{}}
                pageActions={props.pageActions ?? {}}
                onHighlightUnit={props.onHighlightUnit}
                unitsWidth={{}}
                updateEmptyUnits={props.updateEmptyUnits}
                onAddPage={props.onAddPage}
                onRemovePage={onRemovePage}
                isReadOnly={props.isReadOnly}
                onMount={props.onMount}
                onUnmount={props.onUnmount}
                enableUploadFilesInPage={false}
                onRemoveAttachment={onRemoveSingleAttachment}
                showUnitBorder={true}
                offPartialRender>
                <AttachmentFormLayoutContainer
                    {...props}
                    openAttachmentAction={openAttachmentAction}
                    setOpenAttachmentAction={setOpenAttachmentAction}
                    attachmentInfoRef={attachmentInfoRef}
                />
            </SingleUnitContainer>
            {props.children}
        </>
    );
};
